import { useCountryParam, useSort } from 'utils/hooks';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getPathFromNode } from 'utils/helpers';
import isFinite from 'lodash/isFinite';
import objectHash from 'object-hash';
import sortDataByKey from 'utils/sortDataByKey';
import { useCallback } from 'react';
import useGetHierarchy from 'utils/hooks/useGetHierarchy';
import { useQuery } from 'react-query';

import { BreadcrumbPopoverMenu, BreadcrumbSelectMenu } from './GraphButton';
import { Breadcrumbs } from './Breadcrumbs/index';
import { HIDDEN_ROOTS } from '@/config/constants';
import { getBreadCrumbs } from 'utils/formatters';
import { trackNavigation } from './Link';
import { views } from '@/copy';

const createOptionMetrics = ({ metrics, option }) => {
    if (!(metrics && metrics.length)) return [];
    return metrics.map(({ key, label, formatter }) => ({
        metric: formatter(option[key]),
        label,
        isNegative: option[key] < 0,
        unformattedMetric: option[key],
        formatter,
    }));
};

const useBreadcrumbs = ({ id, name, getHierarchy, dataSource, ...options }) =>
    useQuery(
        [
            'breadcrumbs',
            {
                id,
                name,
                dataSource,
                getHierarchyFnId: objectHash(getHierarchy),
            },
        ],
        ({ queryKey }) => {
            const [_key, { name, id, dataSource }] = queryKey;
            return getBreadCrumbs(
                {
                    [name ? 'name' : 'id']: name || id,
                },
                dataSource,
                getHierarchy,
            );
        },
        options,
    );

export const useTrackedNavigate = ({ replace, generateLocation }) => {
    const history = useHistory();
    const location = useLocation();
    const { mutateToWithCountryParam } = useCountryParam();

    return useCallback(
        (category, parentId) => {
            const historyFn = history[replace ? 'replace' : 'push'];
            const to = mutateToWithCountryParam(
                generateLocation(category, parentId),
            );
            trackNavigation(location.pathname, to, 'BreadCrumb', {
                name: category ? category.name : undefined,
            });
            historyFn(to);
        },
        [
            history,
            location,
            mutateToWithCountryParam,
            replace,
            generateLocation,
        ],
    );
};

export function IndustriesSelectMenu({
    id: categoryId,
    isActive,
    generateLocation,
    depth,
    metrics,
    showMetrics,
    industriesQuery,
    ...props
}) {
    const { isError, data: industries } = industriesQuery;

    const value =
        industries && categoryId
            ? industries.find(({ id }) => id === categoryId)
            : undefined;

    const { key: primaryMetricKey } = metrics?.[0] || {};
    const maxMetric = industries
        ? Math.max(
              ...industries
                  .map(({ [primaryMetricKey]: value }) => value)
                  .filter(isFinite),
          )
        : null;

    const options = industries
        ? industries
              .filter(({ name }) => name !== 'ingredients')
              .map((industry) => ({
                  ...industry,
                  metrics: createOptionMetrics({ metrics, option: industry }),
                  showMetrics,
                  percentage: (industry[primaryMetricKey] / maxMetric) * 100,
              }))
        : null;

    if (!industries) return <Breadcrumbs.Breadcrumb isLoading={!isError} />;

    return (
        <div css="display: flex; flex-direction: column;">
            <BreadcrumbSelectMenu
                isActive={isActive}
                value={value}
                options={options}
                mixpanelContext="BreadCrumb"
                mixpanelMeta={{
                    label: 'cat1',
                    depth,
                    ...(value && { name: value.name }),
                }}
                {...(value && { to: generateLocation(value) })}
                defaultOpen
                {...props}
            />
        </div>
    );
}

export function CategorySelectMenu({
    parentId,
    id,
    isActive,
    generateLocation,
    depth,
    metrics,
    showMetrics,
    subcategoriesQuery,
    subcategoryFilter,
    ...props
}) {
    const filteredData =
        subcategoriesQuery.data && subcategoryFilter
            ? subcategoriesQuery.data.filter(subcategoryFilter)
            : subcategoriesQuery.data;

    const value = filteredData
        ? filteredData.find(({ id: subcategoryId }) => subcategoryId === id)
        : null;

    const { key: primaryMetricKey, label } = metrics?.[0] || {};
    const maxMetric = filteredData
        ? Math.max(
              ...filteredData
                  .map(({ [primaryMetricKey]: value }) => value)
                  .filter(isFinite),
          )
        : null;

    const unsortedOptions = filteredData
        ? filteredData
              .filter((subcategory) =>
                  subcategory.hasOwnProperty(primaryMetricKey),
              )
              .map((subcategory) => ({
                  ...subcategory,
                  image: subcategory.image ? subcategory.image[0] : null,
                  metrics: createOptionMetrics({
                      metrics,
                      option: subcategory,
                  }),
                  showMetrics,
                  percentage: (subcategory[primaryMetricKey] / maxMetric) * 100,
              }))
        : null;

    const { sortedData: options } = useSort(unsortedOptions, sortDataByKey, {
        label,
        value: `${label} ${views.descending}`,
        sortBy: primaryMetricKey,
        ascending: false,
    });

    if (
        ['idle', 'loading'].includes(subcategoriesQuery.status) ||
        (subcategoriesQuery.data &&
            subcategoriesQuery.data.some(
                ({ fetching, fetchingData }) => fetching || fetchingData,
            ))
    )
        return <Breadcrumbs.Breadcrumb isLoading={true} />;

    if (
        subcategoryFilter &&
        subcategoriesQuery.data?.length > 0 &&
        filteredData.length === 0
    )
        return null;

    const optionIds =
        options && options.length ? options.map(({ id }) => id).sort() : null;

    const key = optionIds ? optionIds.join('||') : 'no-options';

    return (
        <div css="display: flex; flex-direction: column;" id="cat-two-select">
            <BreadcrumbSelectMenu
                isActive={isActive}
                value={value}
                options={options}
                mixpanelContext="BreadCrumb"
                mixpanelMeta={{
                    label: 'cat2',
                    depth,
                    ...(value && { name: value.name }),
                }}
                {...(value && { to: generateLocation(value) })}
                {...props}
                key={key}
            />
        </div>
    );
}

export function CategoryPopoverMenu({
    country,
    parent,
    parentId,
    id,
    isActive,
    generateLocation,
    depth,
    metrics,
    showMetrics,
    subcategoriesQuery,
    ...props
}) {
    const value = subcategoriesQuery.data
        ? subcategoriesQuery.data.find(
              ({ id: subcategoryId }) => subcategoryId === id,
          )
        : null;

    const { key: primaryMetricKey, label } = metrics?.[0] || {};
    const maxMetric = subcategoriesQuery.data
        ? Math.max(
              ...subcategoriesQuery.data
                  .map(({ [primaryMetricKey]: value }) => value)
                  .filter(isFinite),
          )
        : null;

    const unsortedOptions = subcategoriesQuery.data
        ? subcategoriesQuery.data
              .filter((subcategory) =>
                  subcategory.hasOwnProperty(primaryMetricKey),
              )
              .map((subcategory) => ({
                  ...subcategory,
                  metrics: createOptionMetrics({
                      metrics,
                      option: subcategory,
                  }),
                  showMetrics,
                  percentage: (subcategory[primaryMetricKey] / maxMetric) * 100,
              }))
        : null;

    const { sortedData: options } = useSort(unsortedOptions, sortDataByKey, {
        label,
        value: `${label}} ${views.descending}`,
        sortBy: primaryMetricKey,
        ascending: false,
    });

    if (
        subcategoriesQuery.isFetching ||
        subcategoriesQuery.isLoading ||
        !subcategoriesQuery.data ||
        !parent
    )
        return (
            <Breadcrumbs.Breadcrumb
                isLoading={
                    !!(
                        subcategoriesQuery.isFetching ||
                        subcategoriesQuery.isLoading
                    )
                }
            />
        );

    const onChange =
        parent?.depth === 'cat4'
            ? (option) => {
                  props.onChange(option, parent?.id);
              }
            : props.onChange;

    return (
        <div css="display: flex; flex-direction: column;">
            <BreadcrumbPopoverMenu
                isActive={isActive}
                parent={parent}
                value={value}
                options={options}
                mixpanelContext="BreadCrumb"
                mixpanelMeta={{
                    depth,
                    ...(value && {
                        label: value.label,
                        name: value.name,
                    }),
                }}
                {...(value && { to: generateLocation(value, id) })}
                {...props}
                onChange={onChange}
            />
        </div>
    );
}

export function AffinityCrumb({
    id,
    generateLocation,
    metrics,
    showMetrics,
    subcategoriesQuery,
    categoryQuery,
    country,
    cats,
    parent,
    ...props
}) {
    const value =
        subcategoriesQuery.data?.find(
            ({ id: subcategoryId }) => subcategoryId === id,
        ) ||
        categoryQuery.data ||
        null;
    const { key: primaryMetricKey, label } = metrics?.[0] || {};
    const maxMetric = subcategoriesQuery.data
        ? Math.max(
              ...subcategoriesQuery.data
                  .map(({ [primaryMetricKey]: value }) => value)
                  .filter(isFinite),
          )
        : null;
    const unsortedOptions = subcategoriesQuery.data
        ? subcategoriesQuery.data
              .filter((subcategory) =>
                  subcategory.hasOwnProperty(primaryMetricKey),
              )
              .map((subcategory) => ({
                  ...subcategory,
                  metrics: createOptionMetrics({
                      metrics,
                      option: subcategory,
                  }),
                  showMetrics,
                  percentage: (subcategory[primaryMetricKey] / maxMetric) * 100,
              }))
        : null;
    const { sortedData: options } = useSort(unsortedOptions, sortDataByKey, {
        label,
        value: `${label}} ${views.descending}`,
        sortBy: primaryMetricKey,
        ascending: false,
    });

    if (cats.isError) {
        return <Breadcrumbs.Breadcrumb isLoading={false} />;
    }
    if (!(options && options.length > 0)) {
        const statusIsLoadingOrIdle = (status) =>
            status === 'loading' || status === 'idle';
        const isLoading = [
            cats.status,
            subcategoriesQuery.firstPageQuery?.status,
            ...(subcategoriesQuery.restPageQueries || []).map(
                ({ status }) => status,
            ),
        ].some(statusIsLoadingOrIdle);
        return <Breadcrumbs.Breadcrumb isLoading={isLoading} />;
    }
    if (
        options &&
        options.length > 0 &&
        (subcategoriesQuery.isPlaceholderData ||
            subcategoriesQuery.data?.some(
                ({ fetching, fetchingData }) => fetching || fetchingData,
            ))
    ) {
        return <Breadcrumbs.Breadcrumb isLoading={true} />;
    }

    return (
        <div css="display: flex; flex-direction: column;">
            <BreadcrumbPopoverMenu
                isActive={true}
                parent={parent}
                value={value}
                options={options}
                mixpanelContext="BreadCrumb"
                mixpanelMeta={{
                    ...(value && {
                        label: value.label,
                        name: value.name,
                    }),
                }}
                {...(value && { to: generateLocation(value) })}
                {...props}
            />
        </div>
    );
}

function CategoryCrumbs({
    generateLocation = getPathFromNode,
    showIndustriesMenu = false,
    maxDepth = 5,
    replace = false,
    metrics,
    showMetrics = true,
    showIndustryMetrics,
    industriesQuery,
    useBreadcrumbs: useBreadcrumbsHook = useBreadcrumbs,
    CategoryPopoverMenuComponent,
    CategorySelectMenuComponent,
    AffinityCrumbComponent,
    viewingIndustries,
    defaultOpen = true,
    skeletonSize,
    subcategoryFilter,
    dataSource,
}) {
    const onChange = useTrackedNavigate({ replace, generateLocation });
    const { industryName, categoryId } = useParams();
    const queryEnabled = !!(categoryId || industryName);
    const getHierarchy = useGetHierarchy();
    const crumbsQuery = useBreadcrumbsHook({
        id: categoryId,
        name: industryName,
        getHierarchy,
        dataSource,
        enabled: queryEnabled,
    });

    // Every trail starts at the Industries root, so a trail of nothing else is
    // no trail: the source resolved the category but none of its ancestry.
    const crumbsData = (crumbsQuery.data || []).filter(
        ({ name }) => name !== 'Industries',
    );

    // Two situations land here. A page with no entity in the URL has nothing to
    // show until one is picked, so the picker opens itself. An entity this
    // source cannot place in its taxonomy lands here too, and there the user
    // already asked for a page: a menu opening over it unasked reads as a
    // glitch, so the picker is offered rather than opened.
    if (!queryEnabled || (crumbsQuery.data && crumbsData.length < 1))
        return (
            <Breadcrumbs.Container>
                <IndustriesSelectMenu
                    generateLocation={generateLocation}
                    onChange={onChange}
                    metrics={metrics}
                    showMetrics={
                        showIndustryMetrics === false ? false : showMetrics
                    }
                    industriesQuery={industriesQuery}
                    defaultOpen={defaultOpen && !queryEnabled}
                >
                    {'1. '}
                </IndustriesSelectMenu>
                {new Array(3).fill('').map((_s, idx) => (
                    <Breadcrumbs.Breadcrumb key={`empty-crumb-${idx}`} />
                ))}
            </Breadcrumbs.Container>
        );

    if (['idle', 'loading', 'error'].includes(crumbsQuery.status)) {
        return (
            <Breadcrumbs.Skeleton
                status={crumbsQuery.status}
                size={skeletonSize}
            />
        );
    }

    if (
        crumbsData.some(({ name, depth, hierarchies }) => {
            const { hierarchy_hint } = getHierarchy(hierarchies);
            return (
                HIDDEN_ROOTS.includes(hierarchy_hint?.cat1) ||
                (HIDDEN_ROOTS.includes(name) && depth === 'cat1')
            );
        })
    ) {
        return (
            <Breadcrumbs.Container css="grid-template-columns: minmax(25%, min-content);">
                <AffinityCrumbComponent
                    id={categoryId}
                    generateLocation={generateLocation}
                    onChange={onChange}
                    metrics={metrics}
                    showMetrics={showMetrics}
                />
            </Breadcrumbs.Container>
        );
    }

    const depth = crumbsData.length;

    const hideCatTwo = !!(crumbsData[0] && crumbsData[0].children.length === 1);

    const numEmptyCrumbs = Math.max(
        0,
        maxDepth - crumbsData.length - 1 - (maxDepth < 5 && hideCatTwo),
    );

    return (
        <Breadcrumbs.Container>
            {crumbsData.map((crumb, idx) => {
                const isActive =
                    crumb.id === categoryId || crumb.name === industryName;
                if (idx < 1 && showIndustriesMenu) {
                    return (
                        <IndustriesSelectMenu
                            key={crumb.id}
                            id={crumb.id}
                            isActive={isActive}
                            generateLocation={generateLocation}
                            onChange={onChange}
                            depth={depth}
                            metrics={metrics}
                            showMetrics={
                                showIndustryMetrics === false
                                    ? false
                                    : showMetrics
                            }
                            industriesQuery={industriesQuery}
                        >
                            {`1. ${crumb.name}`}
                        </IndustriesSelectMenu>
                    );
                }

                if (
                    (idx < 1 && !showIndustriesMenu) ||
                    (crumb.depth === 'cat2' && hideCatTwo)
                ) {
                    return null;
                }

                if (crumb.depth === 'cat2') {
                    return (
                        <CategorySelectMenuComponent
                            key={crumb.id}
                            isActive={isActive}
                            parentId={crumbsData[idx - 1].id}
                            id={crumb.id}
                            generateLocation={generateLocation}
                            onChange={onChange}
                            depth={depth}
                            metrics={metrics}
                            showMetrics={showMetrics}
                            subcategoryFilter={subcategoryFilter}
                        >
                            {`${hideCatTwo ? idx : idx + showIndustriesMenu}. ${crumb.name}`}
                        </CategorySelectMenuComponent>
                    );
                }

                return (
                    <CategoryPopoverMenuComponent
                        key={crumb.id}
                        isActive={isActive}
                        parentId={idx - 1 < 0 ? null : crumbsData[idx - 1].id}
                        id={crumb.id}
                        generateLocation={generateLocation}
                        onChange={onChange}
                        depth={depth}
                        metrics={metrics}
                        showMetrics={showMetrics}
                    >
                        {`${idx - hideCatTwo + showIndustriesMenu}. ${crumb.name}`}
                    </CategoryPopoverMenuComponent>
                );
            })}
            {crumbsData[crumbsData.length - 1].depth !== `cat${maxDepth}` ? (
                crumbsData[crumbsData.length - 1].depth === 'cat1' &&
                !hideCatTwo ? (
                    <CategorySelectMenuComponent
                        key={`${crumbsData[crumbsData.length - 1].depth}-subcat`}
                        parentId={
                            hideCatTwo
                                ? crumbsData[crumbsData.length - 1].children[0]
                                : crumbsData[crumbsData.length - 1].id
                        }
                        generateLocation={generateLocation}
                        onChange={onChange}
                        depth={depth}
                        metrics={metrics}
                        showMetrics={showMetrics}
                        defaultOpen={depth === 1 && viewingIndustries}
                        subcategoryFilter={subcategoryFilter}
                    >
                        {`${crumbsData.length - hideCatTwo + showIndustriesMenu}. `}
                    </CategorySelectMenuComponent>
                ) : (
                    <CategoryPopoverMenuComponent
                        key={`${crumbsData[crumbsData.length - 1].depth}-subcat`}
                        parentId={
                            hideCatTwo &&
                            crumbsData[crumbsData.length - 1].depth === 'cat1'
                                ? crumbsData[crumbsData.length - 1].children[0]
                                : crumbsData[crumbsData.length - 1].id
                        }
                        generateLocation={generateLocation}
                        onChange={onChange}
                        depth={depth}
                        metrics={metrics}
                        showMetrics={showMetrics}
                    >
                        {`${crumbsData.length - (hideCatTwo && crumbsData.length > 1) + showIndustriesMenu}. Subcategory`}
                    </CategoryPopoverMenuComponent>
                )
            ) : null}
            {crumbsData[crumbsData.length - 1].depth !== `cat${maxDepth}`
                ? new Array(numEmptyCrumbs)
                      .fill('')
                      .map((_s, idx) => (
                          <Breadcrumbs.Breadcrumb key={`empty-crumb-${idx}`} />
                      ))
                : null}
        </Breadcrumbs.Container>
    );
}

export default CategoryCrumbs;
